/**
 * Types pour la gestion du régime fiscal de l'entreprise (RDC).
 *
 * Le régime fiscal détermine notamment :
 *  - L'assujettissement à la TVA (SMT et FORFAITAIRE en sont exonérés)
 *  - Le taux d'IBP (1% du CA en SMT, 30% du bénéfice en NORMAL)
 *  - Le plan comptable recommandé (SYSCOHADA_MINIMAL vs SYSCOHADA_NORMAL)
 *
 * Source backend : `apps/accounting-service/src/modules/organization/entities/organization.entity.ts`
 */

export type FiscalRegime = 'SMT' | 'NORMAL' | 'FORFAITAIRE';

export const FISCAL_REGIME_LABELS: Record<FiscalRegime, string> = {
  SMT: 'Système Minimal de Trésorerie',
  NORMAL: 'Système Normal',
  FORFAITAIRE: 'Régime Forfaitaire',
};

export const FISCAL_REGIME_DESCRIPTIONS: Record<FiscalRegime, string> = {
  SMT: 'CA < 100M CDF — exonéré de TVA, IBP = 1% du chiffre d\'affaires (comptabilité de trésorerie).',
  NORMAL: 'CA ≥ 100M CDF — assujetti à la TVA 16%, IBP = 30% sur le bénéfice fiscal.',
  FORFAITAIRE: 'Micro-entreprises — exonéré de TVA, imposition forfaitaire.',
};

/** Aperçu d'une transition de régime — réponse de `GET /organization/fiscal-regime/preview` */
export interface FiscalRegimeTransitionPreview {
  currentRegime: FiscalRegime;
  newRegime: FiscalRegime;
  /** La transition est-elle autorisée ? */
  allowed: boolean;
  /** Motif du refus le cas échéant */
  blockingReason?: string;
  /** Vrai si NORMAL → SMT/FORFAITAIRE et qu'il y a déjà des écritures TVA */
  hasVatActivity: boolean;
  /** Plan comptable recommandé après évolution */
  recommendedFramework: 'SYSCOHADA_NORMAL' | 'SYSCOHADA_MINIMAL' | 'IFRS';
  /** Vrai si le plan actuel est déjà cohérent — pas besoin de re-init */
  isFrameworkAligned: boolean;
}

/** Payload pour `PATCH /organization/fiscal-regime` */
export interface ChangeFiscalRegimePayload {
  newRegime: FiscalRegime;
  reason?: string;
}

/** Résultat d'un changement de régime */
export interface ChangeFiscalRegimeResult {
  organization: {
    id: string;
    fiscalRegime: FiscalRegime;
    [key: string]: any;
  };
  preview: FiscalRegimeTransitionPreview;
}

/** Aperçu de la déclaration TVA — réponse de `GET /declarations/preview/vat` */
export interface VatDeclarationPreview {
  period: string;
  fiscalYearId?: string;
  vatCollected: number;
  vatDeductible: number;
  /** Net dû (négatif = crédit de TVA reportable) */
  netDuePayable: number;
  linesAnalyzed: number;
  byTaxCode?: Array<{
    vatCode: string;
    collected: number;
    deductible: number;
  }>;
}
